/**
 * Il suggerimento dell'episodio successivo, che compare verso la fine (SPEC §3.4).
 *
 * ## Perché non è `episodeAfter` e basta
 *
 * Perché la card dice **due cose diverse** secondo dove si è: «PROSSIMO EPISODIO» dentro una
 * stagione, «INIZIA LA STAGIONE 3» quando si è all'ultimo di una. È la stessa informazione per il
 * codice e una promessa diversa per chi guarda — passare alla puntata dopo è ovvio, cominciare una
 * stagione nuova è una decisione — e chi disegna deve poterlo sapere senza rifare il conto.
 *
 * ## Quando compare
 *
 * Quando mancano tre minuti o meno. Non alla fine: alla fine chi guarda ha già preso il telecomando
 * o se n'è andato. Tre minuti sono i titoli di coda di una serie, cioè esattamente il momento in cui
 * si decide se continuare.
 *
 * **E mai mentre si sta scorrendo la barra**: la card si prende il fuoco, e prenderselo a qualcuno
 * che sta cercando una posizione è il modo di far perdere il punto.
 *
 * ## Per i film mai, e non è una dimenticanza
 *
 * Un film non ha un successivo. La cosa più vicina sarebbe un consiglio, che è un'altra funzione con
 * un'altra promessa: «guarda anche» non è «continua».
 */

import type { EpisodeInfo, SeriesDetail } from './seriesDetail.js';
import { episodeAfter } from './seriesDetail.js';

/** Da quanto manca alla fine si propone il successivo. Tre minuti: i titoli di coda. */
export const NEXT_EPISODE_WINDOW_SEC = 180;

export interface NextEpisode {
  readonly episode: EpisodeInfo;
  /**
   * Vero quando il successivo **apre una stagione nuova**.
   *
   * Cambia la frase — «Inizia la stagione 3» invece di «Prossimo episodio» — perché cambia quel che
   * si sta per fare: una puntata dopo l'altra è continuare, una stagione nuova è cominciare.
   */
  readonly startsSeason: boolean;
}

/** L'episodio successivo, o `null` alla fine della serie. */
export function nextEpisodeOf(detail: SeriesDetail, current: EpisodeInfo): NextEpisode | null {
  const after = episodeAfter(detail, current);
  if (after === null) return null;
  return { episode: after, startsSeason: after.season !== current.season };
}

/**
 * Se la card va mostrata adesso.
 *
 * Separata da `nextEpisodeOf` perché sono due domande con due risposte diverse: *quale* è il
 * successivo non cambia mentre si guarda, *se* mostrarlo cambia a ogni secondo. Tenerle insieme
 * vorrebbe dire rifare la ricerca dell'episodio a ogni aggiornamento della posizione.
 */
export function shouldOfferNext(input: {
  readonly hasNext: boolean;
  readonly positionSec: number;
  readonly durationSec: number;
  /** Vero mentre si sta scorrendo la barra: lì la card non deve comparire. */
  readonly scrubbing: boolean;
  /** Vero se chi guarda l'ha già chiusa per questo episodio. */
  readonly dismissed: boolean;
}): boolean {
  if (!input.hasNext || input.scrubbing || input.dismissed) return false;
  // Durata sconosciuta: non si sa quanto manca, e proporre «il prossimo» a metà film sarebbe un
  // invito a smettere di guardare quello che si sta guardando.
  if (!Number.isFinite(input.durationSec) || input.durationSec <= 0) return false;
  const remaining = input.durationSec - input.positionSec;
  return remaining >= 0 && remaining <= NEXT_EPISODE_WINDOW_SEC;
}
